/*
 * @Author: youfanrong 737064439@qq.com
 * @Date: 2025-09-24 
 * @Description: 版本更新检测工具
 */
import { ElMessageBox, ElNotification } from 'element-plus'

interface VersionInfo {
  version: string
  timestamp: number
}

let currentVersion: VersionInfo | null = null
let checkTimer: NodeJS.Timeout | null = null
let hasShownUpdatePrompt = false // 防止重复弹窗

// 获取当前版本信息
async function getCurrentVersion(): Promise<VersionInfo | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-cache'
    })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    return await response.json()
  } catch (error) {
    console.warn('获取版本信息失败:', error)
    return null
  }
}

// 初始化版本信息
export async function initVersionCheck(): Promise<void> {
  try {
    currentVersion = await getCurrentVersion()
    if (currentVersion) {
      console.log('当前版本:', currentVersion)
    }
  } catch (error) {
    console.warn('初始化版本检查失败:', error)
  }
}

// 检查版本更新
export async function checkForUpdates(): Promise<boolean> {
  const latestVersion = await getCurrentVersion()
  
  if (!currentVersion || !latestVersion) {
    return false
  }
  
  // 比较时间戳，如果服务器版本时间戳更新，说明有新版本
  return latestVersion.timestamp > currentVersion.timestamp
}

// 显示更新提示
function showUpdatePrompt(): void {
  if (hasShownUpdatePrompt) {
    return // 防止重复弹窗
  }
  
  hasShownUpdatePrompt = true
  
  ElMessageBox.confirm(
    '检测到系统有新版本更新，为确保您能正常使用最新功能，建议立即刷新页面。',
    '系统更新提示',
    {
      confirmButtonText: '立即刷新',
      cancelButtonText: '稍后提醒',
      type: 'warning',
      showClose: false,
      closeOnClickModal: false,
      closeOnPressEscape: false,
    }
  ).then(() => {
    // 用户选择立即刷新
    window.location.reload()
  }).catch(() => {
    // 用户选择稍后，重置标志位，5分钟后再次检查
    hasShownUpdatePrompt = false
    setTimeout(() => {
      checkForUpdates().then(hasUpdate => {
        if (hasUpdate) {
          showUpdatePrompt()
        }
      })
    }, 5 * 60 * 1000) // 5分钟后再次提醒
  })
}

// 显示更新通知（非阻塞式）
function showUpdateNotification(): void {
  ElNotification({
    title: '版本更新',
    message: '检测到新版本，点击刷新页面体验最新功能',
    type: 'info',
    duration: 0, // 不自动关闭
    onClick: () => {
      window.location.reload()
    }
  })
}

// 启动定期检查
export function startVersionCheck(options: {
  interval?: number // 检查间隔，默认60秒
  mode?: 'modal' | 'notification' // 提示模式，默认modal
  autoStart?: boolean // 是否自动开始检查，默认true
} = {}): void {
  const { 
    interval = 60000, // 默认60秒检查一次
    mode = 'modal',
    autoStart = true 
  } = options

  console.log('startVersionCheck', options)
  console.log('interval', interval)
  console.log('mode', mode)
  console.log('autoStart', autoStart)

  if (!autoStart) {
    return
  }

  // 清除之前的定时器
  if (checkTimer) {
    clearInterval(checkTimer)
  }
  
  checkTimer = setInterval(async () => {
    try {
      const hasUpdate = await checkForUpdates()
      if (hasUpdate) {
        console.log('检测到版本更新')
        
        if (mode === 'modal') {
          showUpdatePrompt()
        } else {
          showUpdateNotification()
        }
        
        // 检测到更新后停止定期检查
        stopVersionCheck()
      }
    } catch (error) {
      console.warn('版本检查失败:', error)
    }
  }, interval)
  
  console.log(`版本检查已启动，检查间隔: ${interval}ms`)
}

// 停止检查
export function stopVersionCheck(): void {
  if (checkTimer) {
    clearInterval(checkTimer)
    checkTimer = null
    console.log('版本检查已停止')
  }
}

// 注意：版本检测是完全自动的，不需要手动操作
// - 每2分钟自动检查一次
// - 页面重新获得焦点时自动检查
// - 检测到更新时自动提示用户刷新

// 页面可见性变化时检查更新
function handleVisibilityChange(): void {
  if (document.visibilityState === 'visible') {
    // 页面从隐藏变为可见时检查更新
    setTimeout(() => {
      checkForUpdates().then(hasUpdate => {
        if (hasUpdate && !hasShownUpdatePrompt) {
          showUpdatePrompt()
        }
      })
    }, 1000) // 延迟1秒检查
  }
}

// 监听页面可见性变化
export function enableVisibilityCheck(): void {
  document.addEventListener('visibilitychange', handleVisibilityChange)
}

// 移除页面可见性监听
export function disableVisibilityCheck(): void {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
}

// 在页面卸载前清理资源
window.addEventListener('beforeunload', () => {
  stopVersionCheck()
  disableVisibilityCheck()
})
